import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { trackEvent } from '../../../lib/analytics';
import { billingAPI, settingsAPI, subscriptionsAPI } from '../../../lib/api';
import { useMixpanel } from '../../../lib/mixpanel';
import type {
  CheckoutReadinessData,
  InviteData,
  MembersResponse,
  SubscriptionData,
} from '../types';

export function useMembers(orgId: string | undefined) {
  return useQuery<MembersResponse>({
    queryKey: ['members', orgId],
    queryFn: async () => {
      const { data } = await settingsAPI.listMembers(orgId!);
      return data;
    },
    enabled: !!orgId,
    staleTime: 5 * 60_000,
  });
}

export function useInvites(orgId: string | undefined) {
  return useQuery<InviteData[]>({
    queryKey: ['invites', orgId],
    queryFn: async () => {
      const { data } = await settingsAPI.listInvites(orgId!, { status: 'pending' });
      return data;
    },
    enabled: !!orgId,
    staleTime: 5 * 60_000,
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { fullName: string }) => settingsAPI.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orgId, name, timezone }: { orgId: string; name: string; timezone?: string }) =>
      settingsAPI.updateOrganization(orgId, { name, ...(timezone && { timezone }) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

export function useSubscription(orgId: string | undefined) {
  return useQuery<SubscriptionData | null>({
    queryKey: ['subscription', orgId],
    queryFn: async () => {
      const { data } = await subscriptionsAPI.getSubscription(orgId!);
      return data;
    },
    enabled: !!orgId,
    staleTime: 5 * 60_000,
  });
}

export function useCheckoutReadiness(orgId: string | undefined, hasAccess = true) {
  return useQuery<CheckoutReadinessData>({
    queryKey: ['billing-checkout-readiness', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization is required for checkout readiness');
      const { data } = await billingAPI.getCheckoutReadiness(orgId);
      return data;
    },
    enabled: !!orgId && hasAccess,
    staleTime: 60_000,
  });
}

export function useSetPendingPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      orgId,
      plan,
      cycle,
    }: {
      orgId: string;
      plan: string;
      cycle: 'monthly' | 'yearly';
    }) => subscriptionsAPI.setPendingPlan(orgId, { plan, cycle }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['subscription', variables.orgId] });
    },
  });
}

export function useCreateInvite() {
  const queryClient = useQueryClient();
  const mp = useMixpanel();

  return useMutation({
    mutationFn: ({ orgId, email, role }: { orgId: string; email: string; role?: string }) =>
      settingsAPI.createInvite(orgId, { email, role }),
    onSuccess: (_data, variables) => {
      trackEvent(mp, 'org.invite_created');
      queryClient.invalidateQueries({ queryKey: ['invites', variables.orgId] });
    },
    onError: (error, variables) => {
      const errorCode = (error as { response?: { data?: { errorCode?: string } } })?.response?.data
        ?.errorCode;
      if (errorCode === 'SEAT_LIMIT_REACHED') {
        queryClient.invalidateQueries({ queryKey: ['subscription', variables.orgId] });
      }
    },
  });
}

export function useRevokeInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orgId, inviteId }: { orgId: string; inviteId: string }) =>
      settingsAPI.revokeInvite(orgId, inviteId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invites', variables.orgId] });
    },
  });
}

export function useUpdateMemberRole() {
  const queryClient = useQueryClient();
  const mp = useMixpanel();

  return useMutation({
    mutationFn: ({ orgId, userId, role }: { orgId: string; userId: string; role: string }) =>
      settingsAPI.updateMemberRole(orgId, userId, role),
    onSuccess: (_data, variables) => {
      trackEvent(mp, 'org.member_role_updated');
      queryClient.invalidateQueries({ queryKey: ['members', variables.orgId] });
    },
  });
}

export function useRemoveMember() {
  const queryClient = useQueryClient();
  const mp = useMixpanel();

  return useMutation({
    mutationFn: ({ orgId, userId }: { orgId: string; userId: string }) =>
      settingsAPI.removeMember(orgId, userId),
    onSuccess: (_data, variables) => {
      trackEvent(mp, 'org.member_removed');
      queryClient.invalidateQueries({ queryKey: ['members', variables.orgId] });
    },
  });
}

export function useSettings() {
  return {
    useMembers,
    useInvites,
    useSubscription,
    useCheckoutReadiness,
    useSetPendingPlan,
    useUpdateProfile,
    useUpdateOrganization,
    useCreateInvite,
    useRevokeInvite,
    useUpdateMemberRole,
    useRemoveMember,
  };
}
